'use client';

import * as React from 'react';
import { Bell, LogOut, MapPinned } from 'lucide-react';
import type { CityCode } from '@loci/city-editions';

import { DialogClose } from '@/components/atoms/primitives/dialog';
import { cn } from '@/components/atoms/primitives/utils';
import { NotificationCountPill } from '@/components/molecules/NotificationCountPill';
import { useCityIsStaging } from '@/lib/city-context.client';
import { crossHostLogin } from '@/lib/cross-host-redirect';
import { appHostLabel, appUrl, cityUrl } from '@/lib/cross-host-urls';
import { helpLegalLinks } from '@/lib/nav/helpLegalLinks';

import { MenuWayfinderCard } from './MenuWayfinderCard';

/**
 * AppMenuSheetContent — surface-aware body for the shared `NavMenuSheet` shell
 * on the signed-in app shell (WEB-628 / DES-60 / WEB-639), mounted by `AppNavMenu`.
 *
 * Overlap resolution with AppHeader + BottomNav (stated in the PR body too):
 * the header band (md+) carries Feed / Discover / Saved, and the
 * mobile dock carries Feed / Discover / Saved / Profile. This menu
 * deliberately omits those hubs and instead surfaces destinations those
 * chrome pieces cannot hold — secondary explore (neighborhoods), the
 * help/legal set, notifications, identity + Sign out, and a carded
 * Wayfinder entry.
 *
 * WEB-639 deliberate omissions (twins with CityMenuSheetContent): Venues /
 * Artists secondary rows (re-add only when those pages launch), footer meta
 * line, inline Wayfinder layers, EXPLORE / WAYFINDER section eyebrows.
 */

const FOCUS_RING = 'focus-visible:outline-none focus-visible:[box-shadow:var(--shadow-ring-teal)]';

const ROW_CLASS = cn(
  'flex min-h-11 items-center gap-3 rounded-ds-md px-3 py-2.5 no-underline',
  'font-display text-[14px] font-medium text-fg-1',
  'transition-colors duration-150 hover:bg-white/[0.06] motion-reduce:transition-none',
  FOCUS_RING,
);

const SECTION_LABEL = 'px-3 pb-1.5 pt-4 font-mono text-micro uppercase tracking-[0.16em] text-fg-3';

export interface AppMenuSheetContentProps {
  readonly cityCode: CityCode;
  /** Display name already resolved by the host header (avoid re-lookup). */
  readonly cityName: string;
  /** When true, render identity + Sign out; otherwise Sign in. */
  readonly isAuthed?: boolean;
  readonly displayName?: string;
  readonly email?: string | null;
  /** Destination for the signed-out Sign-in row. */
  readonly signInHref?: string;
  /**
   * Server-authoritative unread count for the Notifications row pill
   * (WEB-635). Omitted / zero hides the pill entirely — never renders `0`.
   */
  readonly unreadCount?: number;
  /**
   * Opens the shared NotificationPanel after the menu sheet closes
   * (`DialogClose` + store open — no stacked takeovers).
   */
  readonly onOpenNotifications?: () => void;
  /**
   * Called synchronously before the Wayfinder card opens the header sheet,
   * so the host can suppress menu focus-return (WEB-639).
   */
  readonly onBeforeOpenWayfinder?: () => void;
  readonly className?: string;
}

interface SecondaryNavItem {
  readonly key: 'neighborhoods';
  readonly label: string;
  readonly href: string;
  readonly icon: React.ComponentType<{
    className?: string;
    'aria-hidden'?: boolean | 'true' | 'false';
  }>;
}

function buildSecondaryNav(
  cityCode: CityCode,
  isStaging: boolean | undefined,
): readonly SecondaryNavItem[] {
  // WEB-639: Venues + Artists omitted until those pages launch. Re-add rows
  // only then — do not feature-flag or comment them in.
  return [
    {
      key: 'neighborhoods',
      label: 'Neighborhoods',
      href: cityUrl(cityCode, '/neighborhoods', { isStaging }),
      icon: MapPinned,
    },
  ];
}

export function AppMenuSheetContent({
  cityCode,
  cityName,
  isAuthed = false,
  displayName = 'Account',
  email = null,
  signInHref,
  unreadCount = 0,
  onOpenNotifications,
  onBeforeOpenWayfinder,
  className,
}: AppMenuSheetContentProps): React.ReactElement {
  // WEB-826: SSR-seeded staging flag from the root-layout `CityStagingProvider`.
  // Every cross-host string below must be a pure function of it — left to
  // resolve themselves they fall through to `window.location.hostname`, a rung
  // only the browser can reach, and this body then announces `app.loci.city`
  // and links to production while the user is signed in on staging. The direct
  // twin `CityMenuSheetContent` threads the same flag (WEB-776).
  const isStaging = useCityIsStaging();
  const secondary = React.useMemo(
    () => buildSecondaryNav(cityCode, isStaging),
    [cityCode, isStaging],
  );
  const legalLinks = React.useMemo(() => helpLegalLinks({ isStaging }), [isStaging]);
  const signOutHref = appUrl({ city: null, path: '/logout', isStaging });
  const host = appHostLabel(cityCode, { isStaging });

  return (
    <div
      data-testid="app-menu-sheet-content"
      className={cn('flex min-h-0 flex-1 flex-col', className)}
    >
      <div className="flex-1 px-2 pb-4 md:px-3">
        <nav aria-label="Secondary navigation">
          <ul className="flex flex-col gap-0.5" data-testid="app-menu-secondary-nav">
            {secondary.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.key}>
                  <DialogClose asChild>
                    <a
                      href={item.href}
                      data-testid={`app-menu-link-${item.key}`}
                      className={ROW_CLASS}
                    >
                      <Icon className="size-4 shrink-0 text-fg-2" aria-hidden="true" />
                      <span className="truncate">{item.label}</span>
                    </a>
                  </DialogClose>
                </li>
              );
            })}
          </ul>
        </nav>

        {isAuthed ? (
          <>
            <p className={SECTION_LABEL}>Account</p>
            <nav aria-label="Account">
              <ul className="flex flex-col gap-0.5">
                <li>
                  <DialogClose asChild>
                    <button
                      type="button"
                      onClick={onOpenNotifications}
                      data-testid="app-menu-link-notifications"
                      aria-label={
                        unreadCount > 0 ? `Notifications — ${unreadCount} unread` : 'Notifications'
                      }
                      className={cn(ROW_CLASS, 'w-full text-left')}
                    >
                      <Bell className="size-4 shrink-0 text-fg-2" aria-hidden="true" />
                      <span className="min-w-0 flex-1 truncate">Notifications</span>
                      <NotificationCountPill count={unreadCount} />
                    </button>
                  </DialogClose>
                </li>
              </ul>
            </nav>
          </>
        ) : null}

        <p className={SECTION_LABEL}>Help &amp; legal</p>
        <nav aria-label="Help and legal">
          <ul className="flex flex-col gap-0.5">
            {legalLinks.map(({ label, href }) => (
              <li key={href}>
                <DialogClose asChild>
                  <a
                    href={href}
                    data-testid={`app-menu-help-${label.toLowerCase()}`}
                    className={ROW_CLASS}
                  >
                    <span className="truncate">{label}</span>
                  </a>
                </DialogClose>
              </li>
            ))}
          </ul>
        </nav>

        <div className="mt-3">
          <MenuWayfinderCard
            hostLabel={host}
            subtitle="Signed-in home · switch layer or city"
            ariaLabel={`You are here · ${host}. Open Wayfinder to switch layer or city for ${cityName}.`}
            testId="app-menu-wayfinder"
            onBeforeOpen={onBeforeOpenWayfinder}
            className="mx-1 w-[calc(100%-0.5rem)] md:mx-1 md:w-[calc(100%-0.5rem)]"
          />
        </div>
      </div>

      {/* Identity / auth block — pinned to the bottom of the scroll body. */}
      <div
        data-testid="app-menu-auth-block"
        className="shrink-0 border-t border-white/[0.08] px-3 py-4 md:px-4"
      >
        {isAuthed ? (
          <div className="flex flex-col gap-3">
            <div data-testid="app-menu-identity" className="min-w-0">
              <p className="truncate font-display text-body font-semibold text-fg-1">
                {displayName}
              </p>
              {email ? (
                <p className="truncate font-mono text-micro tracking-[0.04em] text-fg-3">{email}</p>
              ) : null}
            </div>
            <DialogClose asChild>
              <a
                href={signOutHref}
                data-testid="app-menu-sign-out"
                className={cn(ROW_CLASS, 'border border-glass-border bg-glass-bg')}
              >
                <LogOut className="size-4 shrink-0 text-fg-2" aria-hidden="true" />
                <span>Sign out</span>
              </a>
            </DialogClose>
          </div>
        ) : (
          <DialogClose asChild>
            <a
              href={signInHref ?? crossHostLogin(undefined, { force: true, isStaging })}
              data-testid="app-menu-sign-in"
              className={cn(ROW_CLASS, 'justify-center border border-glass-border bg-glass-bg')}
            >
              Sign in
            </a>
          </DialogClose>
        )}
      </div>
    </div>
  );
}
